import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "motion/react";
import { X, Download, Share } from "lucide-react";
import { RippleButton } from "./ripple-button";

const DISMISS_KEY = "tqm_pwa_dismissed_at";
const SNOOZE_MS = 7 * 24 * 60 * 60 * 1000;

type InstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
};

function isStandalone() {
  if (typeof window === "undefined") return true;
  const iosStandalone = (window.navigator as Navigator & { standalone?: boolean }).standalone;
  return window.matchMedia("(display-mode: standalone)").matches || iosStandalone === true;
}

function isIos() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && "ontouchend" in document);
}

function recentlyDismissed() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) ?? 0);
    return at > 0 && Date.now() - at < SNOOZE_MS;
  } catch {
    return false;
  }
}

export function InstallPrompt() {
  const [visible, setVisible] = useState(false);
  const [deferred, setDeferred] = useState<InstallPromptEvent | null>(null);
  const [showIosHint, setShowIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone() || recentlyDismissed()) return;

    const onPrompt = (e: Event) => {
      e.preventDefault();
      setDeferred(e as InstallPromptEvent);
      setVisible(true);
    };
    const onInstalled = () => setVisible(false);

    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);

    // iOS never fires beforeinstallprompt — show the banner with manual instructions.
    if (isIos()) setVisible(true);

    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, String(Date.now()));
    } catch {
      /* storage may be unavailable */
    }
    setVisible(false);
  };

  const install = async () => {
    if (deferred) {
      await deferred.prompt();
      const choice = await deferred.userChoice;
      setDeferred(null);
      if (choice.outcome === "accepted") setVisible(false);
      else dismiss();
      return;
    }
    setShowIosHint(true);
  };

  return (
    <AnimatePresence>
      {visible ? (
        <motion.div
          initial={{ y: -24, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: -24, opacity: 0 }}
          transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          className="sticky top-0 z-50 w-full border-b border-hairline bg-background/85 px-3 py-2.5 backdrop-blur-xl"
        >
          <div className="mx-auto flex w-full max-w-4xl items-center gap-3">
            <img
              src="/icon-192.png"
              alt="Quantum Minds logo"
              width={40}
              height={40}
              className="size-10 shrink-0 rounded-xl"
            />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-foreground">
                Install Quantum Minds
              </p>
              <p className="truncate text-xs text-muted-foreground">
                {showIosHint
                  ? "Tap Share → Add to Home Screen"
                  : "Add to your home screen for a better experience."}
              </p>
            </div>
            <RippleButton
              size="sm"
              onClick={install}
              className="flex shrink-0 items-center gap-1.5 rounded-full px-4 text-xs font-semibold"
            >
              {showIosHint ? <Share className="size-3.5" /> : <Download className="size-3.5" />}
              <span>{showIosHint ? "How" : "Install"}</span>
            </RippleButton>
            <button
              type="button"
              onClick={dismiss}
              aria-label="Dismiss install banner"
              className="shrink-0 rounded-full p-1.5 text-muted-foreground transition-colors hover:text-foreground"
            >
              <X className="size-4" />
            </button>
          </div>
        </motion.div>
      ) : null}
    </AnimatePresence>
  );
}
